 <template>
  <div class="pa-3" v-if="paper">
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-alert
      v-if="shouldDisable"
      border="bottom"
      colored-border
      type="error"
      color="#fcba03"
      elevation="2"
    >
      You can no longer edit this paper. The final deadline fot this paper has
      already passed.
    </v-alert>
    <v-form ref="paperForm">
      <v-card class="mb-5">
        <v-toolbar flat color="primary" dark>
          <v-toolbar-title>Paper</v-toolbar-title>
        </v-toolbar>
        <v-tabs vertical left>
          <!--Paper-->
          <v-tab id="paper-submit-info-tab">
            <v-icon left> {{ icon.mdiNoteOutline }} </v-icon>
            Paper
          </v-tab>
          <!--Author-->
          <v-tab id="paper-submit-authors-tab">
            <v-icon left> {{ icon.mdiAccountVoice }} </v-icon>
            Author
          </v-tab>

          <!-- Paper -->
          <v-tab-item>
            <v-card flat class="pa-6">
              <v-text-field
                id="paper-title-input"
                :disabled="shouldDisable"
                :rules="requiredRule"
                outlined
                dense
                label="Title"
                v-model="paper.title"
              ></v-text-field>

              <v-textarea
                id="paper-abstract-input"
                :disabled="shouldDisable"
                :rules="requiredRule"
                outlined
                dense
                label="Abstract"
                v-model="paper.abstract"
              ></v-textarea>

              <span
                class="mb-3"
                style="font-weight: 300"
                v-if="paper.keywords.length"
                >Keywords:
              </span>

              <v-chip
                class="mx-1 mb-3"
                v-for="(keyword, j) in paper.keywords"
                close
                :key="j == 0 ? -1 : (j + 1) * -1"
                @click:close="removeKeyword(j)"
                >{{ paper.keywords[j] }}</v-chip
              >
              <v-text-field
                id="paper-keywords-input"
                :disabled="shouldDisable"
                outlined
                dense
                label="Keywords"
                hint="Write a keyword, press ENTER to add"
                @keypress.enter="addKeyword"
                v-model="currentKeyword"
              ></v-text-field>

              <v-file-input
                id="paper-document-file-input"
                :disabled="shouldDisable"
                :rules="requiredRule"
                prepend-icon=""
                :prepend-inner-icon="icon.mdiPaperclip"
                outlined
                dense
                v-model="file"
                label="Submit your document"
              ></v-file-input>

              <v-row justify="end" class="mx-1 mb-4">
                <v-btn
                  outlined
                  @click="openPdf()"
                  id="paper-document-openpdf-button"
                >
                  Open Current PDF
                  <v-icon color="error"> {{ icon.mdiFilePdfBox }} </v-icon>
                </v-btn>
              </v-row>

              <v-alert
                v-if="trackWarning.length && !shouldDisable"
                border="bottom"
                colored-border
                type="error"
                color="#fcba03"
                elevation="2"
              >
                {{ trackWarning }}
              </v-alert>

              <v-select
                id="paper-track-select"
                :disabled="shouldDisable"
                :rules="requiredRule"
                outlined
                dense
                label="Track"
                :items="shouldDisable ? langTracks : availableTracks"
                item-text="title"
                item-value="id"
                v-model="paper.trackId"
              ></v-select>
            </v-card>
          </v-tab-item>

          <!-- Authors -->
          <v-tab-item>
            <v-card flat class="pa-3">
              <div class="custom-title">Authors</div>

              <v-divider class="mx-5"></v-divider>

              <div v-for="(author, i) in paper.authors" :key="i" class="pa-5">
                <v-row class="mb-2 px-3">
                  <div style="font-weight: 300">Author {{ i + 1 }}</div>

                  <v-spacer></v-spacer>

                  <v-btn
                    :id="'paper-remove-author-button-' + i"
                    color="red"
                    :disabled="paper.authors.length == 1 || shouldDisable"
                    icon
                    @click="removeAuthor(i)"
                    ><v-icon>{{ icon.mdiClose }}</v-icon></v-btn
                  >
                </v-row>

                <v-form ref="authorsForm">
                  <v-text-field
                    :id="'paper-author-name-' + i"
                    :disabled="shouldDisable"
                    :rules="requiredRule"
                    outlined
                    dense
                    label="Name"
                    v-model="paper.authors[i].name"
                  ></v-text-field>

                  <v-text-field
                    :id="'paper-author-email-' + i"
                    :disabled="shouldDisable"
                    :rules="requiredRule"
                    outlined
                    dense
                    label="Email"
                    v-model="paper.authors[i].email"
                  ></v-text-field>

                  <v-text-field
                    :id="'paper-author-country-' + i"
                    :disabled="shouldDisable"
                    :rules="requiredRule"
                    outlined
                    dense
                    label="Country"
                    v-model="paper.authors[i].country"
                  ></v-text-field>

                  <v-text-field
                    :id="'paper-author-orcid-' + i"
                    :disabled="shouldDisable"
                    :rules="requiredRule"
                    outlined
                    dense
                    label="Orcid"
                    v-model="paper.authors[i].orcid"
                  ></v-text-field>
                </v-form>
              </div>
              <v-row justify="end" class="mx-1">
                <v-btn
                  id="paper-add-author-button"
                  color="blue"
                  dark
                  @click="addAuthor()"
                  :disabled="shouldDisable"
                  >Add author
                  <v-icon right>{{ icon.mdiPlus }}</v-icon>
                </v-btn>
              </v-row>
            </v-card>
          </v-tab-item>
        </v-tabs>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import { getSubmitPaperTemplate } from "@/helpers/emailTemplates/submitPaper.js";
import {
  mdiAccountVoice,
  mdiNoteOutline,
  mdiPlus,
  mdiPaperclip,
  mdiClose,
  mdiFilePdfBox,
  mdiAlertOutline,
} from "@mdi/js";
import paper from "@/helpers/paperStatus.js";

export default {
  props: ["dialog", "paper"],
  data: () => ({
    icon: {
      mdiNoteOutline,
      mdiAccountVoice,
      mdiPlus,
      mdiPaperclip,
      mdiClose,
      mdiFilePdfBox,
      mdiAlertOutline,
    },
    authorAux: {
      name: "",
      email: "",
      country: "",
      orcid: "",
    },
    currentKeyword: "",
    requiredRule: [(v) => !!v || "Field required"],
    file: null,
    loading: false,
  }),
  methods: {
    addAuthor() {
      this.paper.authors.push(Object.assign({}, this.authorAux));
    },
    removeAuthor(index) {
      this.paper.authors.splice(index, 1);
    },
    async save() {
      let paperVal = this.$refs.paperForm.validate();

      let authorsVal = [];
      this.paper.authors.forEach((auth, i) => {
        authorsVal.push(this.$refs.authorsForm[i].validate());
      });

      if (!paperVal || authorsVal.find((auth) => auth == false) !== undefined)
        return;

      if (this.paper.keywords.length == 0) {
        alert("At least one keyword is necessary");
        return;
      }

      this.loading = true;
      this.paper.lastUpdate = new Date().toLocaleString("en-US");

      await this.$store
        .dispatch("createPaper", this.paper)
        .then(async (paperId) => {
          //add paperId to user papersPublished
          if (
            await this.$store.dispatch("addPublishedId", {
              id: this.user.id,
              element: paperId,
            })
          ) {
            this.$store.dispatch("updateChangeLogs", {
              paperId: paperId,
              log: {
                label: `${paper.changeLog("", "")["create"].label}`,
                datetime: new Date().toLocaleString("en-US"),
              },
            }),
              this.$store.dispatch("setSnackbar", {
                show: true,
                text: "Success on save Paper!",
                color: "success",
              });
          } else {
            this.$store.dispatch("setSnackbar", {
              show: true,
              text: "Oops... Error on save Paper",
              color: "error",
            });
          }

          //add 'res' role to user if not in roles array
          if (!this.user.roles.includes("res")) {
            let newUser = Object.assign({}, this.user);
            newUser.roles.push("res");
            await this.$store.dispatch("updateUserRole", newUser);
          }

          if (this.file) {
            //POPULATE EMAIL TEMPLATE//
            var temp = getSubmitPaperTemplate({
              conferenceTitle: this.$i18n.t("HOME.title"),
              conferenceDate: this.$i18n.t("HOME.date"),
              contentTitle: this.$i18n.t("MAILER.submitPaper.contentTitle"),
              paperTitle: this.$i18n.t("MAILER.submitPaper.paperTitle"),
              abstract: this.$i18n.t("MAILER.submitPaper.abstractTitle"),
              paper: {
                title: this.paper.title,
                abstract: this.paper.abstract,
              },
              register: this.$i18n.t("MAILER.submitPaper.register"),
            });
            //CONVERT ARRAY OF EMAILS TO STRING//
            var emails = this.paper.authors.map((item) => {
              return item["email"];
            });
            emails.toString();

            //UPLOAD PAPER AND SEND EMAIL//
            await this.$store.dispatch("uploadPaper", {
              file: this.file,
              name: paperId,
              template: temp,
              from: `${this.$i18n.t("HOME.title")} <jornadasihc2021@gmail.com>`,
              to: emails,
              subject: `${this.$i18n.t("MAILER.submitPaper.emailTitle")}`,
            });
          }
          this.loading = false;
          this.$router.push("/dashboard/researcher").catch();
        });
      await this.$store.dispatch("getPapers"); //refresh papers array

      this.loading = false;
    },
    addKeyword() {
      this.paper.keywords.push(this.currentKeyword);
      this.currentKeyword = "";
    },
    removeKeyword(index) {
      this.paper.keywords.splice(index, 1);
    },
    reset() {
      //TODO: scroll back to top

      this.paper = {
        title: "",
        abstract: "",
        keywords: [],
        trackId: "",
        lastUpdate: "",
        authors: [
          {
            name: "",
            email: "",
            country: "",
            orcid: "",
          },
        ],
        state: {
          status: "Unassigned",
          reviews: {},
        },
        logs: [],
      };

      this.authors = [
        {
          name: "",
          email: "",
          country: "",
          orcid: "",
        },
      ];
      this.currentKeyword = "";
      this.file = null;
      this.loading = false;
    },
    async openPdf() {
      let url = await this.$store.dispatch("getPaperPdf", {
        name: this.paper.id,
      });

      window.open(url);
    },
  },
  computed: {
    snackbar() {
      return this.$store.state.snackbar.snackbar;
    },
    tracks() {
      return this.$store.getters.getTracks || [];
    },
    user() {
      return this.$store.getters.getUser;
    },
    langTracks() {
      let lang = this.$i18n.locale;

      return this.tracks.map((track) => {
        return {
          id: track.id,
          title: track.content[lang].title,
          deadlines: track.content[lang].deadlines,
        };
      });
    },
    availableTracks() {
      let today = new Date();

      return this.langTracks.filter((track) => {
        let initialDl = track.deadlines.find((t) => t.type == "Initial"); //get initial deadline

        if (initialDl) {
          let maxTime = Date.parse(
            initialDl.schedule.day + " " + initialDl.schedule.time
          ); //get time from initial dl date
          if (today.getTime() > maxTime) {
            //if today is more recent than date deadline has passed
            return false;
          }

          return track; //otherwise return track as available
        }

        return track; //if no initial use track as available -> TODO: maybe change this to false later
      });
    },
    trackWarning() {
      let trackId = this.paper.trackId;
      let track = this.langTracks.find((t) => t.id == trackId);
      let lang = this.$i18n.locale;

      if (trackId && track) {
        switch (lang) {
          case "en":
            return `You current track is: "${track.title}". The initial deadline for this track has already passed, if you change it now you cannot go back.`;
          case "pt":
            return `Sua trilha atual é: "${track.title}". O prazo de entrega inicial dessa trilha já passou, se você mudá-la agora você não poderá voltar nela.`;
          case "es":
            return `Tu session actual es: "${track.title}". Ha pasado el tiempo de entrega inicial de esta session, si la cambias ahora no podrás volver a ella.`;
        }
      }

      return "";
    },
    shouldDisable() {
      let trackId = this.paper.trackId;
      let track = this.langTracks.find((t) => t.id == trackId);
      let finalDl = track?.deadlines?.find((t) => t.type == "Final"); //get Final deadline

      let maxTime = Date.parse(
        finalDl?.schedule?.day + " " + finalDl?.schedule?.time
      ); //get time from final dl date
      let today = new Date();

      let status = this.paper?.state?.status;
      let isRevisionStatus =
        status == "Accepted with Revision" ||
        status == "Rejected with Revision";

      if (today.getTime() > maxTime && !isRevisionStatus) return true; //if Final date has passed and status is not with resvision

      return false;
    },
  },
  async created() {
    if (!this.$store.getters.getTracks) await this.$store.dispatch("getTracks");
  },
};
</script>

<style scoped>
.custom-title {
  font-weight: 500;
  font-size: 1.35rem;
  margin-left: 20px;
}
</style>